import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

// IMPORT FILE SVG ASLI KAMU (YANG HALUS)
import { ReactComponent as HeroVector } from './hero_vector.svg';

const dynamicLines = [
  "soft portraits, quiet moments.",
  "selected works from jakarta, sometimes bali.",
  "a softer side outside professional life.",
  "curated frames with intention.",
  "light, atmosphere, and carefully seen moments."
];

export default function Hero() {
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false); 
      setTimeout(() => {
        setCurrentLineIndex((prev) => (prev + 1) % dynamicLines.length);
        setFade(true); 
      }, 500); 
    }, 3200);
    return () => clearInterval(interval);
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.2 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
  };

  return (
    <section className="relative w-full h-[100dvh] md:snap-start flex flex-col items-center justify-center bg-white text-butter-text overflow-hidden">
      
      <div className="max-w-[1280px] w-full mx-auto px-6 md:px-12 lg:px-16 flex items-center justify-center relative z-10 h-full">
        
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 lg:gap-16 w-full items-center pt-10 md:pt-0" 
        >
          
          {/* BAGIAN KANAN - Sketsa Vector Asli Ivan */}
          <div className="md:col-span-6 md:col-start-7 flex justify-center md:justify-end order-1 md:order-2">
            <div className="w-[75%] md:w-full max-w-[320px] md:max-w-[480px] aspect-square flex items-center justify-center relative">
              
              {/* --- SOFT AIRBRUSH MASK REVEAL --- */}
              <svg viewBox="0 0 1000 1000" className="w-full h-full text-butter-text">
                <defs>
                  {/* 1. FILTER BLUR: Membuat ujung spidol menjadi kabur/lembut (airbrush effect) */}
                  <filter id="softBrush" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="60" />
                  </filter>

                  {/* Membuat topeng pelindung */}
                  <mask id="softBrushMask" maskUnits="userSpaceOnUse" x="0" y="0" width="1000" height="1000">
                    
                    {/* 2. PATH MELENGKUNG: Menggunakan kurva bezier (C dan S) agar pergerakannya mulus */}
                    <path 
                      className="mask-anim-path"
                      d="M -100,100 C 300,-100 700,300 1100,100 S 700,500 -100,500 S 700,700 1100,600 S 700,900 -100,900 S 700,1100 1100,1000"
                      fill="none"
                      stroke="white"
                      strokeWidth="350"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      filter="url(#softBrush)" /* Menyuntikkan efek airbrush ke coretan ini */
                    />
                  </mask>
                </defs>

                {/* SVG aslimu dibungkus oleh topeng lembut di atas */}
                <g mask="url(#softBrushMask)">
                  {/* Panggil komponen aslimu dengan paksaan ukuran 1000px agar klop dengan maskernya */}
                  <HeroVector width="1000" height="1000" color="currentColor" />
                </g>
              </svg>

            </div>
          </div>

          {/* BAGIAN KIRI - Teks */}
          <div className="md:col-span-5 flex flex-col order-2 md:order-1 text-center md:text-left">
            <motion.span variants={itemVariants} className="font-butter-sans text-[10px] lg:text-[12px] uppercase tracking-[0.2em] text-butter-muted mb-3 md:mb-6">
              photography / visual archive
            </motion.span>
            
            <motion.h1 variants={itemVariants} className="font-butter-serif text-[36px] md:text-[60px] lg:text-[76px] leading-tight mb-2 text-butter-text">
              buttersando.
            </motion.h1>
            
            <motion.p variants={itemVariants} className="font-butter-sans text-[15px] md:text-[19px] lg:text-[21px] text-butter-text mb-1">
              a visual archive by ivan sridana
            </motion.p>
            
            <motion.div variants={itemVariants} className="h-6 md:h-12 mb-4 md:mb-8 overflow-hidden">
              <p className={`font-butter-sans italic text-[14px] md:text-[19px] lg:text-[21px] text-butter-accent transition-all duration-500 ${fade ? 'opacity-100' : 'opacity-0'}`}>
                {dynamicLines[currentLineIndex]}
              </p>
            </motion.div>
            
            <motion.p variants={itemVariants} className="font-butter-sans text-[13px] md:text-[16px] lg:text-[17px] leading-relaxed text-butter-muted mb-6 md:mb-12 max-w-md mx-auto md:mx-0">
              portraits, visual notes, and selected moments from in and around my days.
            </motion.p>
            
            <motion.span variants={itemVariants} className="font-butter-sans text-[11px] md:text-[13px] text-butter-muted tracking-widest italic opacity-80">
              based in jakarta, sometimes bali
            </motion.span>
          </div>

        </motion.div>
      </div>

      {/* DRIFT DEEPER PROMPT */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-4 md:bottom-12 left-0 md:left-12 lg:left-16 w-full md:w-auto flex flex-row items-center justify-center md:justify-start gap-3 z-20 px-6"
      >
        <span className="font-butter-sans text-[8px] md:text-[10px] uppercase tracking-[0.3em] text-butter-text font-medium">
          drift deeper
        </span>
        
        <div className="w-12 md:w-24 h-[1px] bg-butter-line relative overflow-hidden">
          <motion.div 
            animate={{ x: ['-100%', '300%'], opacity: [0, 1, 0] }}
            transition={{ repeat: Infinity, duration: 2.5, ease: "easeInOut" }}
            className="w-1/3 h-full bg-butter-text"
          />
        </div>

        <motion.svg 
          animate={{ y: [0, 3, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="w-3 h-3 text-butter-text" 
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </motion.svg>
      </motion.div>

    </section>
  );
}